import React from 'react';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { TextInputArea } from '@uhg-abyss/web/ui/TextInputArea';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { styled } from '@uhg-abyss/web/tools/styled';
import { SharedDialog } from '../../../common/SharedDialog';
import { useQuestionEditor } from './hooks/useQuestionEditor';
import { useQuestionTypes } from './hooks/useQuestionTypes';
import { validateEditorState, hasErrors } from './api/questionValidators';
import { emptyEditorState } from './api/questionMappers';
import { navigate } from '../../../common/navigate';
import { PageSpinner } from '../../../common/PageSpinner';
import {
  OPTION_BASED_TYPES,
  INPUT_FORMAT_TYPES,
  LENGTH_VALIDATED_TYPES,
  BRANCHING_ENABLED_TYPES,
} from './constants';
import type { QuestionOptionDraft } from './types';

const INPUT_LABEL_COLOR = '$web.semantic.color.text.content.primary';

const textInputCss = {
  'abyss-text-input-root': {
    width: '100%',
    minWidth: '12rem',
  },
  'abyss-text-input-label': {
    color: INPUT_LABEL_COLOR,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  'abyss-text-input': {
    minHeight: '2.5rem',
    fontSize: '0.95rem',
  },
} as const;

const selectInputCss = {
  'abyss-select-input-root': {
    width: '100%',
    minWidth: '12rem',
  },
  'abyss-select-input-label': {
    color: INPUT_LABEL_COLOR,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  'abyss-select-input-container': {
    minHeight: '2.5rem',
    borderRadius: '8px',
    border: '1px solid #b6c9d9',
    boxSizing: 'border-box' as const,
  },
  'abyss-select-input-selection-container': {
    minHeight: '2.5rem',
    padding: '0 0.75rem',
    fontSize: '0.95rem',
  },
} as const;

const Page = styled('section', {
  padding: '1.5rem',
  maxWidth: '860px',
  display: 'grid',
  gap: '1.25rem',
});

const PageHeader = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
});

const FormSection = styled('div', {
  display: 'grid',
  gap: '0.75rem',
  padding: '1rem',
  borderRadius: '8px',
  border: '1px solid #c8d8e6',
  backgroundColor: '#f5f9fc',
});

const SectionTitle = styled(Text, {
  margin: 0,
  fontWeight: 700,
  fontSize: '0.9rem',
  color: '#1a3a5c',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
});

const FieldGroup = styled('div', {
  display: 'grid',
  gap: '0.25rem',
});

const ErrorMsg = styled(Text, {
  margin: 0,
  fontSize: '0.8rem',
  color: '#c0392b',
});

const CheckboxRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
});

const TwoCol = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '0.75rem',
});

const OptionRowWrapper = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr auto auto',
  alignItems: 'center',
  gap: '0.5rem',
});

const ActionsRow = styled('div', {
  display: 'flex',
  gap: '0.75rem',
  alignItems: 'center',
  flexWrap: 'wrap',
});

const ValidationSummary = styled('div', {
  padding: '0.9rem 1rem',
  borderRadius: '8px',
  border: '1px solid #e2a8a2',
  backgroundColor: '#fff4f2',
});

const VALIDATION_FIELD_IDS: Record<string, string> = {
  questionTitle: 'q-title',
  questionTypeId: 'q-type',
  surveyQuestionDbCode: 'q-db-code',
  inputFormat: 'q-input-format',
  minLength: 'q-min-length',
  maxLength: 'q-max-length',
  helpLink: 'q-help-link',
  options: 'q-add-option',
};

const VALIDATION_FIELD_ORDER = [
  'questionTitle',
  'questionTypeId',
  'surveyQuestionDbCode',
  'inputFormat',
  'minLength',
  'maxLength',
  'helpLink',
  'options',
] as const;

function getQuestionIdFromPath(): number | undefined {
  if (typeof window === 'undefined') return undefined;
  const segments = window.location.pathname.split('/').filter(Boolean);
  // /admin/questions/:questionId/edit  → segments = ['admin','questions','123','edit']
  const idx = segments.indexOf('questions');
  if (idx === -1) return undefined;
  const raw = segments[idx + 1];
  if (!raw || raw === 'new') return undefined;
  const n = parseInt(raw, 10);
  return isNaN(n) ? undefined : n;
}

function getSurveyIdFromSearch(): number | undefined {
  if (typeof window === 'undefined') return undefined;
  const v = new URLSearchParams(window.location.search).get('surveyId');
  const n = v ? parseInt(v, 10) : NaN;
  return isNaN(n) ? undefined : n;
}

function focusFirstInvalidField(errors: Record<string, string>) {
  if (typeof document === 'undefined') return;

  for (const key of VALIDATION_FIELD_ORDER) {
    if (!errors[key]) continue;
    const elementId = VALIDATION_FIELD_IDS[key];
    const element = elementId ? document.getElementById(elementId) : null;
    if (element instanceof HTMLElement) {
      element.focus();
      element.scrollIntoView?.({ block: 'center' });
    }
    return;
  }
}

// eslint-disable-next-line complexity -- The page composes independent conditional form sections.
export const QuestionEditorPage = () => {
  const questionId = React.useMemo(() => {
    return getQuestionIdFromPath();
  }, []);
  const surveyId = React.useMemo(() => {
    return getSurveyIdFromSearch();
  }, []);
  const isEdit = !!questionId;

  const {
    editorState,
    setEditorState,
    isLoading,
    isSaving,
    isDirty,
    error,
    save,
  } = useQuestionEditor(questionId);
  const {
    questionTypes,
    inputFormats,
    isLoading: refLoading,
  } = useQuestionTypes();

  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [validationErrors, setValidationErrors] = React.useState<
    Record<string, string>
  >({});
  const [savedTitle, setSavedTitle] = React.useState<string | null>(null);
  const [branchingNoSurveyError, setBranchingNoSurveyError] =
    React.useState(false);

  React.useEffect(() => {
    if (!hasErrors(validationErrors)) {
      return;
    }

    focusFirstInvalidField(validationErrors);
  }, [validationErrors]);

  const typeId = editorState.questionTypeId;
  const showOptions = typeId ? OPTION_BASED_TYPES.has(typeId) : false;
  const showInputFormat = typeId ? INPUT_FORMAT_TYPES.has(typeId) : false;
  const showLength = typeId ? LENGTH_VALIDATED_TYPES.has(typeId) : false;
  const showBranchingButton =
    isEdit && typeId ? BRANCHING_ENABLED_TYPES.has(typeId) : false;
  const questionTypeOptions = React.useMemo(() => {
    return questionTypes.map((type) => {
      return { label: type.questionType, value: type.questionTypeId };
    });
  }, [questionTypes]);
  const inputFormatOptions = React.useMemo(() => {
    return inputFormats.map((format) => {
      return { label: format.label, value: String(format.inputFormatId) };
    });
  }, [inputFormats]);

  const set = (field: string, value: unknown) => {
    return setEditorState((prev) => {
      return { ...prev, [field]: value };
    });
  };

  const addOption = () => {
    const next: QuestionOptionDraft = {
      optionTitle: '',
      orderId: editorState.options.length + 1,
      active: true,
    };
    set('options', [...editorState.options, next]);
  };

  const updateOption = (idx: number, value: string) => {
    const updated = editorState.options.map((o, i) => {
      return i === idx ? { ...o, optionTitle: value } : o;
    });
    set('options', updated);
  };

  const removeOption = (idx: number) => {
    const updated = editorState.options
      .filter((_, i) => {
        return i !== idx;
      })
      .map((o, i) => {
        return { ...o, orderId: i + 1 };
      });
    set('options', updated);
  };

  const handleSave = async (opts?: { configureBranching?: boolean }) => {
    const errors = validateEditorState(editorState);
    if (hasErrors(errors)) {
      setSubmitError(null);
      setValidationErrors(errors as Record<string, string>);
      return;
    }
    setValidationErrors({});
    setSubmitError(null);

    // If branching was requested but no survey context exists, save first then
    // show a blocking error — branching is always survey-scoped.
    if (opts?.configureBranching && !surveyId) {
      try {
        await save({ surveyId }); // save without branching redirect
        setBranchingNoSurveyError(true);
      } catch (err) {
        setSubmitError((err as Error).message ?? 'Save failed');
      }
      return;
    }

    try {
      const result = await save({ ...opts, surveyId });
      if (result.nextPath) {
        // branching flow — skip confirmation and navigate directly
        navigate(result.nextPath);
      } else {
        setSavedTitle(editorState.questionTitle.trim());
      }
    } catch (err) {
      setSubmitError((err as Error).message ?? 'Save failed');
    }
  };

  const handleGoToList = () => {
    const qs = surveyId ? `?surveyId=${surveyId}` : '';
    navigate(`/admin/questions${qs}`);
  };

  const handleCreateAnother = () => {
    setSavedTitle(null);
    setEditorState(emptyEditorState());
    setSubmitError(null);
    setValidationErrors({});
    // Reset URL to /admin/questions/new so the page is clean
    window.history.replaceState({}, '', '/admin/questions/new');
  };

  if (isLoading || refLoading) {
    return (
      <Page>
        <PageSpinner label="Loading question" />
      </Page>
    );
  }
  if (error) {
    return (
      <Page>
        <Text style={{ color: '#c0392b' }}>Error: {error.message}</Text>
      </Page>
    );
  }

  const backPath = surveyId
    ? `/admin/questions?surveyId=${surveyId}`
    : '/admin/questions';
  const validationSummaryMessage = isEdit
    ? 'Please correct the highlighted fields before saving the question.'
    : 'Please correct the highlighted fields before creating the question.';

  return (
    <Page>
      <PageHeader>
        <Button
          variant="text"
          aria-label="Back to questions"
          onClick={() => {
            return navigate(backPath);
          }}
          css={{ 'abyss-button-root': { paddingLeft: 0 } }}
        >
          <IconSymbol icon="arrow_back" size={18} />
        </Button>
        <Heading level={1} style={{ margin: 0, fontSize: '1.4rem' }}>
          <span
            style={{ fontWeight: 400, color: '#345570', cursor: 'pointer' }}
            onClick={() => {
              return window.location.assign(backPath);
            }}
          >
            Questions
          </span>
          {' \u2192 '}
          {isEdit ? 'Edit question' : 'New question'}
        </Heading>
      </PageHeader>

      {hasErrors(validationErrors) && (
        <ValidationSummary role="alert" aria-live="assertive">
          <Text style={{ margin: 0, fontWeight: 600, color: '#8f2d22' }}>
            {validationSummaryMessage}
          </Text>
        </ValidationSummary>
      )}

      {/* Section 1: Identity */}
      <FormSection>
        <SectionTitle>Question identity</SectionTitle>

        <FieldGroup>
          <TextInput
            id="q-title"
            label="Question title"
            isRequired
            value={editorState.questionTitle}
            css={textInputCss}
            onChange={(e) => {
              return set('questionTitle', e.target.value);
            }}
            errorMessage={validationErrors.questionTitle}
          />
        </FieldGroup>

        <TwoCol>
          <FieldGroup>
            <SelectInput
              id="q-type"
              label="Question type"
              isRequired
              value={editorState.questionTypeId ?? ''}
              css={selectInputCss}
              onChange={(value) => {
                return set('questionTypeId', value ? Number(value) : null);
              }}
              options={questionTypeOptions}
              placeholder="Select type"
              errorMessage={validationErrors.questionTypeId}
            />
          </FieldGroup>

          <FieldGroup>
            <TextInput
              id="q-db-code"
              label="DB code"
              isRequired
              value={editorState.surveyQuestionDbCode}
              css={textInputCss}
              onChange={(e) => {
                return set('surveyQuestionDbCode', e.target.value);
              }}
              errorMessage={validationErrors.surveyQuestionDbCode}
            />
          </FieldGroup>
        </TwoCol>

        <TwoCol>
          <CheckboxRow>
            <Checkbox
              inputId="q-required"
              label="Required"
              isChecked={editorState.required}
              onChange={(e) => {
                return set('required', e.target.checked);
              }}
            />
          </CheckboxRow>
          <CheckboxRow>
            <Checkbox
              inputId="q-active"
              label="Active"
              isChecked={editorState.active}
              onChange={(e) => {
                return set('active', e.target.checked);
              }}
            />
          </CheckboxRow>
        </TwoCol>
      </FormSection>

      {/* Section 2: Input behavior */}
      {(showInputFormat || showLength) && (
        <FormSection>
          <SectionTitle>Input behavior and validation</SectionTitle>

          {showInputFormat && (
            <FieldGroup>
              <SelectInput
                id="q-input-format"
                label="Input format"
                value={editorState.inputFormat}
                css={selectInputCss}
                onChange={(value) => {
                  return set('inputFormat', value ? String(value) : '');
                }}
                options={inputFormatOptions}
                placeholder="None"
                isClearable
              />
            </FieldGroup>
          )}

          {showLength && (
            <TwoCol>
              <FieldGroup>
                <TextInput
                  id="q-min-length"
                  label="Min length"
                  value={editorState.minLength}
                  type="number"
                  css={textInputCss}
                  onChange={(e) => {
                    return set('minLength', e.target.value);
                  }}
                  inputMode="numeric"
                  errorMessage={validationErrors.minLength}
                />
              </FieldGroup>
              <FieldGroup>
                <TextInput
                  id="q-max-length"
                  label="Max length"
                  value={editorState.maxLength}
                  type="number"
                  css={textInputCss}
                  onChange={(e) => {
                    return set('maxLength', e.target.value);
                  }}
                  inputMode="numeric"
                  errorMessage={validationErrors.maxLength}
                />
              </FieldGroup>
            </TwoCol>
          )}

          <FieldGroup>
            <TextInput
              id="q-err-msg"
              label="Error message"
              value={editorState.errorMessage}
              css={textInputCss}
              onChange={(e) => {
                return set('errorMessage', e.target.value);
              }}
            />
          </FieldGroup>
        </FormSection>
      )}

      {/* Section 3: Help content */}
      <FormSection>
        <SectionTitle>Help content</SectionTitle>
        <FieldGroup>
          <TextInputArea
            label="Help text"
            value={editorState.helpText}
            onChange={(e) => {
              return set('helpText', e.target.value);
            }}
            css={{ 'abyss-text-input-area': { height: '96px' } }}
          />
        </FieldGroup>
        <TwoCol>
          <FieldGroup>
            <TextInput
              id="q-help-link"
              label="Help link URL"
              type="text"
              value={editorState.helpLink}
              css={textInputCss}
              onChange={(e) => {
                return set('helpLink', e.target.value);
              }}
              errorMessage={validationErrors.helpLink}
            />
          </FieldGroup>
          <FieldGroup>
            <TextInput
              id="q-help-link-label"
              label="Help link label"
              value={editorState.helpLinkLabel}
              css={textInputCss}
              onChange={(e) => {
                return set('helpLinkLabel', e.target.value);
              }}
            />
          </FieldGroup>
        </TwoCol>
      </FormSection>

      {/* Section 4: Options */}
      {showOptions && (
        <FormSection>
          <SectionTitle>Options</SectionTitle>
          {/* TODO(testability): if option-row coverage stays brittle, add a stable field identifier around each draft option input instead of relying only on hidden-label semantics. */}
          <div>
            {editorState.options.map((opt, idx) => {
              return (
                <OptionRowWrapper key={idx} style={{ marginBottom: '0.5rem' }}>
                  <TextInput
                    label={`Option ${idx + 1}`}
                    hideLabel
                    value={opt.optionTitle}
                    css={textInputCss}
                    onChange={(e) => {
                      return updateOption(idx, e.target.value);
                    }}
                    placeholder={`Option ${idx + 1}`}
                    aria-label={`Option ${idx + 1} title`}
                  />
                  <Text
                    style={{ margin: 0, fontSize: '0.8rem', color: '#666' }}
                  >
                    #{opt.orderId}
                  </Text>
                  <Button
                    variant="text"
                    size="sm"
                    aria-label={`Remove option ${idx + 1}`}
                    onClick={() => {
                      return removeOption(idx);
                    }}
                  >
                    <IconSymbol icon="cancel" size={16} />
                  </Button>
                </OptionRowWrapper>
              );
            })}
          </div>
          {validationErrors.options && (
            <ErrorMsg role="alert">{validationErrors.options}</ErrorMsg>
          )}
          <Button id="q-add-option" variant="outline" size="sm" onClick={addOption}>
            <IconSymbol icon="add" size={14} /> Add option
          </Button>
        </FormSection>
      )}

      {submitError && (
        <ErrorMsg role="alert" style={{ fontSize: '0.9rem' }}>
          {submitError}
        </ErrorMsg>
      )}

      <ActionsRow>
        <Button
          onClick={() => {
            return handleSave();
          }}
          isDisabled={isSaving || !isDirty}
        >
          {isSaving ? 'Saving…' : isEdit ? 'Save changes' : 'Create question'}
        </Button>

        {showBranchingButton && (
          <Button
            variant="outline"
            onClick={() => {
              return handleSave({ configureBranching: true });
            }}
            isDisabled={isSaving || !isDirty}
          >
            Save and configure branching
          </Button>
        )}

        <Button
          variant="outline"
          onClick={() => {
            return navigate(backPath);
          }}
          isDisabled={isSaving}
        >
          Cancel
        </Button>
      </ActionsRow>

      <SharedDialog
        mode="app"
        isOpen={savedTitle !== null}
        onClose={handleGoToList}
        title={isEdit ? 'Question updated' : 'Question created'}
        description={
          <React.Fragment>
            <strong>&ldquo;{savedTitle}&rdquo;</strong> has been{' '}
            {isEdit ? 'updated' : 'saved'} successfully.
          </React.Fragment>
        }
        primaryAction={{ label: 'Ok', onClick: handleGoToList }}
        {...(!isEdit && {
          secondaryAction: {
            label: 'Create another survey question',
            variant: 'outline',
            onClick: handleCreateAnother,
          },
        })}
      />

      {isSaving && <PageSpinner label="Saving question" />}

      <SharedDialog
        mode="error"
        isOpen={branchingNoSurveyError}
        onClose={() => {
          return navigate(backPath);
        }}
        title="Survey context required"
        description="Your question was saved. To configure branching, open this question from within a specific survey — branching rules are always scoped to a survey."
        primaryAction={{
          label: 'Go to questions list',
          onClick: () => {
            return navigate(backPath);
          },
        }}
      />
    </Page>
  );
};
